import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { defineStore } from 'pinia'
import { useQueryApp } from 'hooks/apolloQuery.js'
import { enrichPropsContentImages } from 'src/services/image-content/propsContentImages.js'
import getLpAchievements from '@/apollo/queries/getLpAchievements.gql'
import getLpActiveSeason from '@/apollo/queries/getLpActiveSeason.gql'
import getLpLevels from '@/apollo/queries/getLpLevels.gql'
import getLpNextSeason from '@/apollo/queries/getLpNextSeason.gql'
import getLpSeasonCityLevel from '@/apollo/queries/getLpSeasonCityLevel.gql'
import getLpSeasonTemporaryLevel from '@/apollo/queries/getLpSeasonTemporaryLevel.gql'
import getLpUserBalance from '@/apollo/queries/getLpUserBalance.gql'

export const useLoyaltyStore = defineStore('loyalty-store', () => {
  const route = useRoute()

  const activeSeason = ref(null)
  const getLpActiveSeasonQuery = ref(null)
  const activeSeasonPropsContent = computed(() => {
    const propsContent = activeSeason.value.propsContent.find((el) => el.type === 'season_card_type')
    if (propsContent) {
      return JSON.parse(propsContent.props)
    }
    return null
  })

  const nextSeason = ref(null)
  const getLpNextSeasonQuery = ref(null)

  const userBalance = ref()
  const getLpUserBalanceQuery = ref(null)

  const getLpLevelsQuery = ref(null)
  const levels = ref()

  const getLpSeasonCityLevelQuery = ref(null)
  const getLpSeasonTemporaryLevelQuery = ref(null)
  const seasonLevel = ref(null)
  const temporaryLevel = ref(null)

  const isUserBalanceLoaded = ref(false)
  const isActiveSeasonLoaded = ref(false)
  const isLevelsLoaded = ref(false)

  const isLoyaltyBanned = ref(false)
  const isLoyaltyInitStarted = ref(false)

  const isLoyaltyParticipant = computed(() => Boolean(userBalance.value))

  const isLoyaltyLoaded = computed(() => {
    if (!isUserBalanceLoaded.value || !isActiveSeasonLoaded.value) {
      return false
    }
    if (isLoyaltyParticipant.value && activeSeason.value) {
      return isLevelsLoaded.value
    }
    return true
  })

  const isLoyaltyReady = computed(() => !isLoyaltyInitStarted.value || isLoyaltyLoaded.value)

  const isLoyaltyPromoHidden = computed(() => {
    if (!isLoyaltyInitStarted.value) {
      return false
    }
    return !isLoyaltyLoaded.value || isLoyaltyBanned.value
  })

  const rawOlpBalance = computed(() => userBalance.value?.olpBalance?.amount ?? 0)
  const rawXpBalance = computed(() => userBalance.value?.xpBalance?.amount ?? 0)

  // Achievements
  const achievementsData = ref([])
  const achievementsMetaData = ref({})
  const isAchievementsLoading = ref(false)
  let achievementsQuery = null
  let achievementsFetched = false

  let seasonCheckInterval = null

  function formatBalance(value) {
    const num = Number(value)
    if (isNaN(num)) {
      return '0'
    }
    if (num === 0) {
      return '0'
    }

    if (num <= 10) {
      const truncated = Math.floor(num * 100) / 100
      return Number.isInteger(truncated) ? String(truncated) : truncated.toFixed(2)
    }

    if (num > 10 && num < 100) {
      const truncated = Math.floor(num * 10) / 10
      return Number.isInteger(truncated) ? String(truncated) : truncated.toFixed(1)
    }

    const truncated = Math.floor(num)

    return String(truncated)
  }

  const olpBalance = computed(() => formatBalance(rawOlpBalance.value))
  const xpBalance = computed(() => formatBalance(rawXpBalance.value))

  const lvlsWithNumLvl = computed(() => levels.value?.filter((el) => el.numLevel >= 0) || [])

  const userLevel = computed(() => {
    const reachedLevels = lvlsWithNumLvl.value.filter(
      (el) => el.numLevel !== 0 && el.availableByDep && el.requiredXp <= rawXpBalance.value,
    )

    return reachedLevels[reachedLevels.length - 1]
  })

  const userNextLevel = computed(() =>
    lvlsWithNumLvl.value.find((el) => el.numLevel !== 0 && el.availableByDep && el.requiredXp > rawXpBalance.value),
  )

  function fetchLevels(seasonId, withUserData) {
    const query = useQueryApp(getLpLevels, { seasonId, withUserData })
    getLpLevelsQuery.value = query

    query.onError(() => {
      isLevelsLoaded.value = true
    })

    query.onResult((queryResult) => {
      if (queryResult?.loading) {
        return
      }

      isLevelsLoaded.value = true

      if (query.result.value) {
        levels.value = query.result.value.lpLevels
          .map((el) => {
            let expandedProps = el.propsContent.find((prop) => prop.type === 'season_level_card_type')
            if (expandedProps && expandedProps.props) {
              expandedProps = JSON.parse(expandedProps.props)
            }
            return {
              ...el,
              expandedProps,
            }
          })
          .sort((a, b) => {
            const aLevel = a.numLevel
            const bLevel = b.numLevel

            const isValid = (val) => typeof val === 'number' && val > 0

            if (isValid(aLevel) && isValid(bLevel)) {
              return aLevel - bLevel
            }
            if (isValid(aLevel)) {
              return -1
            }
            if (isValid(bLevel)) {
              return 1
            }
            return 0
          })
      }
    })

    return query
  }

  function fetchSeasonLevel() {
    const query = useQueryApp(getLpSeasonCityLevel)
    getLpSeasonCityLevelQuery.value = query

    query.onResult(() => {
      const raw = query.result.value?.lpSeasonCityLevel
      if (!raw) {
        seasonLevel.value = null
        return
      }
      let expandedProps = raw.propsContent.find((prop) => prop.type === 'season_level_card_type')
      if (expandedProps && expandedProps.props) {
        expandedProps = JSON.parse(expandedProps.props)
      }
      seasonLevel.value = { ...raw, expandedProps }
    })

    return query
  }

  function fetchTemporaryLevel() {
    const query = useQueryApp(getLpSeasonTemporaryLevel)
    getLpSeasonTemporaryLevelQuery.value = query

    query.onResult(() => {
      const raw = query.result.value?.lpTempTasksLevel
      if (!raw) {
        temporaryLevel.value = null
        return
      }
      let expandedProps = raw.propsContent.find((prop) => prop.type === 'season_level_card_type')
      if (expandedProps && expandedProps.props) {
        expandedProps = JSON.parse(expandedProps.props)
      }
      temporaryLevel.value = { ...raw, expandedProps }
    })

    return query
  }

  function checkSeasonChange() {
    const nextSeasonStart = nextSeason.value?.activeSettings?.activeStart

    if (!nextSeasonStart) {
      return
    }

    const nextSeasonDate = new Date(nextSeasonStart)
    const now = new Date()

    if (now >= nextSeasonDate) {
      const currentPath = route?.path ?? window.location.pathname
      const isLoyaltyPath = /(^|\/)loyalty(\/|$)/.test(currentPath)

      if (isLoyaltyPath) {
        window.location.reload()
      } else {
        clearInterval(seasonCheckInterval)
        seasonCheckInterval = null
        initLoyalty({ force: true })
      }
    }
  }

  function startSeasonChecker() {
    if (seasonCheckInterval) {
      clearInterval(seasonCheckInterval)
    }

    seasonCheckInterval = setInterval(checkSeasonChange, 1000)
  }

  function fetchNextSeason() {
    const query = useQueryApp(getLpNextSeason, null, { fetchPolicy: 'network-only' })
    getLpNextSeasonQuery.value = query

    query.onResult(({ data }) => {
      if (data?.lpNextSeason) {
        nextSeason.value = data.lpNextSeason
        startSeasonChecker()
      }
    })

    return query
  }

  function initLoyalty({ force = false } = {}) {
    if (isLoyaltyInitStarted.value && !force) {
      return
    }

    isLoyaltyInitStarted.value = true
    isUserBalanceLoaded.value = false
    isActiveSeasonLoaded.value = false
    isLevelsLoaded.value = false
    isLoyaltyBanned.value = false

    const balanceReady = new Promise((resolve) => {
      const queryBalance = useQueryApp(getLpUserBalance, null, { fetchPolicy: 'network-only' })
      getLpUserBalanceQuery.value = queryBalance

      queryBalance.onResult(({ data }) => {
        if (data?.lpUserBalance) {
          userBalance.value = { ...data.lpUserBalance }
          isUserBalanceLoaded.value = true
          resolve(true)
        } else {
          isUserBalanceLoaded.value = true
          resolve(false)
        }
      })

      queryBalance.onError(() => {
        isLoyaltyBanned.value = true
        isUserBalanceLoaded.value = true
        resolve(false)
      })
    })

    const querySeason = useQueryApp(getLpActiveSeason, null, { fetchPolicy: 'network-only' })
    getLpActiveSeasonQuery.value = querySeason

    querySeason.onError(() => {
      activeSeason.value = null
      isActiveSeasonLoaded.value = true
      isLevelsLoaded.value = true
    })

    querySeason.onResult(async ({ data }) => {
      activeSeason.value = data?.lpActiveSeason ?? null
      isActiveSeasonLoaded.value = true

      const seasonId = Number(data?.lpActiveSeason?.id)
      const hasBalance = await balanceReady

      if (seasonId && hasBalance) {
        fetchLevels(seasonId, true)
        fetchSeasonLevel()
        fetchTemporaryLevel()
      } else {
        isLevelsLoaded.value = true
      }
    })

    fetchNextSeason()
  }

  function updateUserOlpBalance(olp) {
    if (userBalance.value) {
      userBalance.value = {
        ...userBalance.value,
        olpBalance: {
          ...userBalance.value.olpBalance,
          amount: olp,
        },
      }
    }
  }

  function updateUserXpBalance(xp) {
    if (userBalance.value) {
      userBalance.value = {
        ...userBalance.value,
        xpBalance: {
          ...userBalance.value.xpBalance,
          amount: xp,
        },
      }
    }
  }

  async function enrichAchievementsImages(achievements) {
    const propsContentLists = achievements.flatMap((achievement) => [
      achievement?.baseAchievement?.propsContent,
      achievement?.currentAchievement?.propsContent,
    ])

    await enrichPropsContentImages(propsContentLists)
  }

  function fetchAchievements() {
    if (achievementsQuery) {
      return achievementsQuery
    }
    isAchievementsLoading.value = true

    achievementsQuery = useQueryApp(getLpAchievements, null, {
      fetchPolicy: 'cache-first',
    })

    achievementsQuery.onResult(async ({ data }) => {
      if (data && !achievementsFetched) {
        const achievements = JSON.parse(JSON.stringify(data?.lpAchievements?.achievements ?? []))
        await enrichAchievementsImages(achievements)
        achievementsData.value = achievements
        achievementsMetaData.value = data?.lpAchievements.meta ?? {}
        isAchievementsLoading.value = false
        achievementsFetched = true
      }
    })

    achievementsQuery.onError(() => {
      isAchievementsLoading.value = false
    })

    return achievementsQuery
  }

  function updateAchievement(updatedAchievement) {
    achievementsData.value = achievementsData.value.map((achievement) => {
      if (+achievement.baseAchievement.id === +updatedAchievement.baseAchievement.id) {
        return {
          ...achievement,
          ...updatedAchievement,
        }
      }
      return achievement
    })
  }

  function updateAchievementUserTask(userTaskId, userTaskData) {
    achievementsData.value = achievementsData.value.map((achievement) => {
      if (achievement.currentAchievement.userTask && Array.isArray(achievement.currentAchievement.userTask)) {
        return {
          ...achievement,
          currentAchievement: {
            ...achievement.currentAchievement,
            userTask: achievement.currentAchievement.userTask.map((task) => {
              if (+task.id === userTaskId) {
                return userTaskData
              }
              return task
            }),
          },
        }
      }
      return achievement
    })
  }

  function incrementDoneAchievements() {
    achievementsMetaData.value = {
      ...achievementsMetaData.value,
      done: achievementsMetaData.value.done + 1,
    }
  }

  function updateUserTaskInLevels(id, data) {
    if (levels.value) {
      levels.value = levels.value.map((level) => {
        return {
          ...level,
          categories: level.categories.map((category) => {
            return {
              ...category,
              tasks: category.tasks.map((task) => {
                return {
                  ...task,
                  userTask: task.userTask.map((userTask) => {
                    if (+userTask.id === id) {
                      return data
                    }
                    return userTask
                  }),
                }
              }),
            }
          }),
        }
      })
    }
  }

  return {
    initLoyalty,
    formatBalance,
    getLpUserBalanceQuery,
    getLpActiveSeasonQuery,
    getLpNextSeasonQuery,
    getLpLevelsQuery,
    getLpSeasonCityLevelQuery,
    getLpSeasonTemporaryLevelQuery,
    fetchSeasonLevel,
    fetchTemporaryLevel,
    seasonLevel,
    temporaryLevel,
    isActiveSeasonLoaded,
    activeSeason,
    activeSeasonPropsContent,
    nextSeason,
    userBalance,
    rawXpBalance,
    rawOlpBalance,
    xpBalance,
    olpBalance,
    isLoyaltyLoaded,
    isLoyaltyBanned,
    isLoyaltyInitStarted,
    isLoyaltyParticipant,
    isLoyaltyReady,
    isLoyaltyPromoHidden,
    levels,
    isUserBalanceLoaded,
    isLevelsLoaded,
    lvlsWithNumLvl,
    userLevel,
    userNextLevel,
    updateUserTaskInLevels,
    updateUserOlpBalance,
    updateUserXpBalance,
    // Achievements
    achievementsData,
    achievementsMetaData,
    isAchievementsLoading,
    fetchAchievements,
    updateAchievementUserTask,
    updateAchievement,
    incrementDoneAchievements,
  }
})
